// quickStudyPdf — converte um PDF em uma lista de imagens JPEG comprimidas,
// uma por página, prontas pra anexar como QuickStudyImageInput. Pra "tirar
// dúvida" o aluno pode mandar PDF de prova/lista e a IA recebe cada página
// como imagem multimodal (mais robusto que tentar extrair texto, porque
// captura tabelas, fórmulas e diagramas).
//
// Carrega PDF.js do CDN lazily (mesmo padrão de AiStudyView).

import type { QuickStudyImageInput } from "./api";

const PDFJS_CDN = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379";

function loadScript(src: string, isModule = false): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    if (isModule) s.type = "module";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Falha ao carregar ${src}`));
    document.head.appendChild(s);
  });
}

async function getPdfjs(): Promise<any> {
  const w = window as any;
  if (!w.pdfjsLib) {
    await loadScript(`${PDFJS_CDN}/pdf.min.mjs`, true);
  }
  const lib = w.pdfjsLib;
  if (!lib) throw new Error("Falha ao carregar PDF.js");
  lib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/pdf.worker.min.mjs`;
  return lib;
}

export interface PdfPagePreview {
  pageNumber: number;
  thumbDataUrl: string;
  /** Largura em px da renderização thumb (pra UI escolher página). */
  width: number;
  height: number;
}

/** Renderiza thumbnails de TODAS as páginas — usado pelo picker quando o PDF
 *  tem várias páginas e o aluno precisa escolher quais mandar. Renderização
 *  pequena (scale=0.4) pra ser rápida mesmo em PDF de 30 páginas. */
export async function renderPdfThumbnails(
  file: File,
  opts: { maxPages?: number } = {}
): Promise<{ totalPages: number; thumbs: PdfPagePreview[] }> {
  const pdfjs = await getPdfjs();
  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const totalPages: number = doc.numPages;
  const cap = Math.min(totalPages, opts.maxPages ?? 50);
  const thumbs: PdfPagePreview[] = [];
  for (let i = 1; i <= cap; i++) {
    const page = await doc.getPage(i);
    const viewport = page.getViewport({ scale: 0.4 });
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas sem contexto 2D.");
    await page.render({ canvasContext: ctx, viewport }).promise;
    thumbs.push({
      pageNumber: i,
      thumbDataUrl: canvas.toDataURL("image/jpeg", 0.75),
      width: canvas.width,
      height: canvas.height
    });
  }
  return { totalPages, thumbs };
}

/** Renderiza as páginas escolhidas em alta resolução e comprime cada uma em
 *  JPEG (~1600px no maior lado, q=0.85) — mesmo formato que
 *  compressImageForQuickStudy produz. Cap de 5 páginas por chamada pra
 *  proteger custo. */
export async function rasterizePdfPages(
  file: File,
  pages: number[],
  opts: { maxPages?: number; maxDim?: number; quality?: number } = {}
): Promise<QuickStudyImageInput[]> {
  const max = opts.maxPages ?? 5;
  const maxDim = opts.maxDim ?? 1600;
  const quality = opts.quality ?? 0.85;
  const pdfjs = await getPdfjs();
  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const totalPages: number = doc.numPages;
  const wanted = pages
    .filter((p) => Number.isInteger(p) && p >= 1 && p <= totalPages)
    .slice(0, max);
  if (wanted.length === 0) return [];

  const out: QuickStudyImageInput[] = [];
  for (const pageNum of wanted) {
    const page = await doc.getPage(pageNum);
    // Renderiza grande primeiro pra preservar nitidez; depois reduz se passou
    // do maxDim. Escala 2.0 = ~150 DPI, suficiente pra texto pequeno de prova.
    const viewport = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas sem contexto 2D.");
    await page.render({ canvasContext: ctx, viewport }).promise;

    // Redimensiona se passou do maxDim.
    let finalCanvas = canvas;
    if (canvas.width > maxDim || canvas.height > maxDim) {
      const ratio = canvas.width >= canvas.height
        ? maxDim / canvas.width
        : maxDim / canvas.height;
      const w = Math.round(canvas.width * ratio);
      const h = Math.round(canvas.height * ratio);
      const small = document.createElement("canvas");
      small.width = w;
      small.height = h;
      const sctx = small.getContext("2d");
      if (!sctx) throw new Error("Canvas sem contexto 2D.");
      sctx.drawImage(canvas, 0, 0, w, h);
      finalCanvas = small;
    }

    const blob: Blob = await new Promise((resolve, reject) =>
      finalCanvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Falha ao gerar JPEG."))),
        "image/jpeg",
        quality
      )
    );
    const base64 = await blobToBase64(blob);
    out.push({
      mimeType: "image/jpeg",
      base64,
      sizeBytes: blob.size
    });
  }
  return out;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result as string;
      const idx = r.indexOf(",");
      resolve(idx >= 0 ? r.slice(idx + 1) : r);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader falhou."));
    reader.readAsDataURL(blob);
  });
}
